import {useContext, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import ReCaptcha from 'react-google-invisible-recaptcha';

import validateNRIC from '../../libs/validateNRIC';
import useLocalStorage from '../../hooks/useLocalStorage';
import {getMember, submitData} from '../../libs/api';

import {
    getLocalItem,
    invalidQR,
    isObjectEmpty,
    removeLocalItem,
    removeLocalItems,
    setLocalItem
} from '../../libs/helpers';
import {
    EXCLUSIVE_MEMBER_ONLY,
    NON_ACTIVE_MEMBER,
    SUPPLEMENTARY_USER,
    TRY_AGAIN_IN_15_MINUTES,
    UNABLE_TO_PROCESS
} from '../../libs/messages';
import {AppContext} from "../../context/AppContext";

const Form = ({title, data, onSubmit, onError, recaptchaKey}) => {
    const [shouldRemember, setShouldRemember] = useLocalStorage('remember', true);
    const [inputType, setInputType] = useState('nric');
    const [userInput, setUserInput] = useState('');
    const [inputError, setInputError] = useState('');

    const history = useHistory();

    const {appData} = useContext(AppContext);

    const inputRef = useRef();
    let recaptcha = useRef();

    const typeSelection = ({target}) => {
        const {value} = target;
        setInputType(value);

        inputRef.current.focus();

        setLocalItem('input-type', value);
    };

    const handleInputChange = ({target}) => {
        const {value} = target;
        const pattern = /^[0-9a-zA-Z]+$/;

        if (inputError) {
            setInputError('');
        }

        let input = value.split(' ').join('');

        if (input.match(pattern) || input === '') {
            if (inputType !== 'passport') {
                if (String(input).length > 9) {
                    input = String(input).substr(0, 9);
                }
            }

            setUserInput(input);
            setLocalItem(`input`, input);
        }
    };

    const remember = ({target}) => {
        const {checked} = target;
        setShouldRemember(checked);

        if (checked) {
            setLocalItem(`input`, userInput);
            setLocalItem(`input-type`, inputType);
        }

        inputRef.current.focus();
    };

    async function onResolve() {
        const captchaToken = recaptcha.current.getResponse();

        await processMemberData(captchaToken);
    }

    const submit = () => {
        if (userInput) {
            onSubmit();
            const isValid = validateNRIC(userInput, inputType);

            if (inputType === 'passport') {
                recaptcha.current.execute();
            } else {
                if (!isValid || isValid === -1) {
                    onSubmit(false);
                    setInputError(
                        `Invalid ${
                            inputType === 'passport'
                                ? 'Passport'
                                : String(inputType).toUpperCase()
                        }`
                    );
                    inputRef.current.focus();
                } else {
                    recaptcha.current.execute();
                }
            }
        } else {
            inputRef.current.focus();
        }
    };

    const processMemberData = async (captchaToken) => {
        try {
            const payload = {
                captchaToken: captchaToken,
                sessionID: appData.sessionID,
                id: userInput && userInput !== "" ? userInput : document.getElementById('userInput') ? document.getElementById('userInput').value : '',
            };

            const member = await getMember(payload);

            // if (!shouldRemember) {
            // 	removeLocalItems(['input', 'input-type']);
            // }

            if (member.error) {
                return onError(member.message);
            } else {
                const memberData = member.data;

                if (!isObjectEmpty(memberData)) {
                    // If we have error from CRM
                    if (member.hasError) {
                        console.log("ERROR from CRM: ", member.errorMessages);
                        return onError(UNABLE_TO_PROCESS);
                    } else {
                        let entity = memberData.entity || {};

                        //Skip Supplementary users as they will not have active cards
                        /*if (entity?.isSupplementary === "true") {
                            return onError(SUPPLEMENTARY_USER);
                        }*/

                        if (entity && entity?.membershipCardList?.length) {
                            const cardsList = entity.membershipCardList;
                            const selectedCard = cardsList.reduce((maxItem, currentItem) => {
                                if (currentItem.canNumber && (!maxItem || new Date(currentItem.expiryDate) > new Date(maxItem.expiryDate))) {
                                    return currentItem;
                                }
                                return maxItem;
                            }, null);

                            if (selectedCard) {
                                return await directSubmit(selectedCard, entity.pauId);
                            }
                            return onError(NON_ACTIVE_MEMBER);
                        }
                        if (entity?.isSupplementary === "true" && entity.pauId !== "") {
                                return await directSubmit([], entity.pauId);
                        }
                        if (entity?.isSupplementary === "true") {
                            return onError(SUPPLEMENTARY_USER);
                        }
                    }
                }
                return onError(EXCLUSIVE_MEMBER_ONLY);
            }
        } catch
            (e) {
            console.error(e);
            return onError(TRY_AGAIN_IN_15_MINUTES);
        }
    };

    const directSubmit = async (card, pauId) => {
        const _data = appData;
        if (_data) {
            data = Object.assign({}, _data);

            if(card){
                data.canNumber = card.canNumber;
                data.cardType = card.cardCode;
                data.card = card;
            }
            data.input = getLocalItem('input');
            data.PAuid = pauId;

            const result = await submitData(data);
            if (result.data) {
                const remember = getLocalItem('remember');
                if (!remember) {
                    removeLocalItems(['input', 'input-type', 'selected-card', 'cards']);
                }

                //we submit to auresys to note the PAUID, it will not give points to user so we will show error if user is supplementary
                if(card === null || (Array.isArray(card) && card.length === 0 && pauId != "")){
                    return onError(SUPPLEMENTARY_USER);
                }else{
                    history.push('/step/2', {from: 'STEP1'});
                }
            } else {
                console.log("ERROR while submitting data:", result.message);
                onError(result.message || UNABLE_TO_PROCESS);
            }
        } else {
            onError(invalidQR());
        }
    };

    useEffect(() => {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});

        const _inputType = getLocalItem('input-type') || 'nric';
        const _userInput = getLocalItem('input') || '';

        if (shouldRemember) {
            setInputType(_inputType);
            setLocalItem('input-type', _inputType);
            setUserInput(_userInput);
        } else {
            setInputType('nric');
            setLocalItem('input-type', 'nric');
            removeLocalItem('input');
        }
        setShouldRemember(shouldRemember);
    }, []);

    return (
        <>
            <ReCaptcha
                ref={recaptcha}
                onResolved={onResolve}
                sitekey={recaptchaKey}
            />

            <div className="row justify-content-center">
                <div className="col-md-10">
                    <h1
                        style={{
                            fontWeight: `bold`,
                            fontSize: `1.5rem`,
                            textAlign: `center`,
                        }}
                    >
                        {title}
                    </h1>
                    <p style={{fontSize: `16px`, textAlign: `center`}}>
                        The PAssion CARES initiative rewards PAssion
                        Members and allows them to contribute to a good cause simply by
                        participating in community events. </p>
                    <p style={{fontSize: `14px`, textAlign: `center`}}>
                        10 yuu Points* will be contributed to community projects by
                        yuu Rewards Club and POSB while another 10 yuu Points
                        will be credited to your yuu Account in appreciation of your support towards PAssion CARES.
                    </p>

                    <form onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}>
                        <p className="type-header">
                            To participate, enter your
                            <br/>
                            <label htmlFor="nric" style={{marginRight: `10px`}}>
                                <input
                                    id="inputType"
                                    name="type"
                                    value="nric"
                                    type="radio"
                                    checked={inputType === 'nric'}
                                    onChange={typeSelection}
                                />{' '}
                                NRIC
                            </label>
                            <label htmlFor="fin" style={{marginRight: `10px`}}>
                                <input
                                    id="inputType"
                                    name="type"
                                    value="fin"
                                    type="radio"
                                    checked={inputType === 'fin'}
                                    onChange={typeSelection}
                                />{' '}
                                FIN
                            </label>
                            <label htmlFor="passport" style={{marginRight: `10px`}}>
                                <input
                                    id="inputType"
                                    name="type"
                                    value="passport"
                                    type="radio"
                                    checked={inputType === 'passport'}
                                    onChange={typeSelection}
                                />{' '}
                                Passport
                            </label>
                        </p>
                        <div className="controls d-flex justify-content-center flex-column">
                            {inputError && (
                                <div
                                    style={{
                                        padding: `0.3rem 0.5rem`,
                                        fontSize: `13px`,
                                        textAlign: `center`,
                                    }}
                                    className="alert alert-danger"
                                    role="alert"
                                >
                                    {inputError}
                                </div>
                            )}
                            {inputType === 'fin' && (
                                <div className="controls d-flex justify-content-center p-2">
                                    <label
                                        className="form-check-label"
                                        htmlFor="flexCheckDefault"
                                        style={{
                                            paddingLeft: `5px`, textAlign: `center`, color: `red`,
                                            marginTop: `-15px`, marginBottom: `8px`, fontWeight: 600
                                        }}
                                    >For PAssion POSB Debit Card holders, please select and key in Passport number
                                        instead. </label>
                                </div>
                            )}
                            <input
                                ref={inputRef}
                                value={userInput}
                                id="userInput"
                                type="text"
                                className={`form-control input-text`}
                                autoComplete={`off`}
                                placeholder={`Enter your ${
                                    inputType === 'passport'
                                        ? `Passport`
                                        : String(inputType).toUpperCase()
                                } here`}
                                onChange={handleInputChange}
                                maxLength={12}
                            />
                        </div>
                        <div className="controls d-flex justify-content-center p-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="flexCheckDefault"
                                defaultChecked={shouldRemember}
                                onChange={remember}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="flexCheckDefault"
                                style={{paddingLeft: `5px`}}
                            >
                                Remember my ID Number
                            </label>
                        </div>
                        <div
                            style={{fontSize: `14px`, textAlign: `center`}}
                            className="p-2"
                        >
                            By submitting, I acknowledge that I understand that PAssion CARES
                            initiative is exclusive to PAssion Members, I acknowledge and agree
                            that my ID number will be used for sole purpose of retrieving my
                            PAssion Membership details, in order to submit my participation for
                            the PAssion CARES programme. <br/>
                            <br/>
                            <div style={{fontSize: `12px`, textAlign: `center`}} className="p-2">
                                *Points awarded for the programme is subjected to change based on prevailing
                                campaigns.
                            </div>
                        </div>
                        <div className="app-button-submit mb-4 mt-1">
                            <div className="col-md-4 m-auto">
                                <div className="d-grid gap-2">
                                    <button
                                        className={`btn btn-block btn-danger btn-lg`}
                                        type={"submit"}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Form;